import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Dialog, Drawer, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import type { SubwayStation } from '@/api/repository/types/types';
import { designSystem } from '@/styles/theme';
import { StationResultList, stationLabel, useStationSearch } from './stationSearch';
import type { StationSearchState } from './stationSearch';

/** 세미나 폼과 같은 색 — 상자형 입력·청록 강조·연청록 선택 */
const COLOR = {
  text: '#1F2A28',
  sub: '#4F5B58',
  muted: '#66736F',
  placeholder: '#9AA5A2',
  border: '#D3DBD8',
  lineSoft: '#F0F3F2',
  primary: designSystem.colors.primary, // #5A9B96
  primaryDeep: '#2F6B66',
  primarySoft: '#E6F1EF',
} as const;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 6px;
`;

/** 알약 칩(36px) — 고른 역은 연청록 바탕·청록 테두리 */
const Chip = styled.button<{ selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${({ selected }) => (selected ? COLOR.primary : COLOR.border)};
  background: ${({ selected }) => (selected ? COLOR.primarySoft : '#fff')};
  color: ${({ selected }) => (selected ? COLOR.primaryDeep : COLOR.text)};
  border-radius: 18px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ selected }) => (selected ? 700 : 500)};
  white-space: nowrap;

  svg {
    width: 16px;
    height: 16px;
  }

  small {
    font-size: 12px;
    font-weight: 500;
    color: ${COLOR.sub};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

/** 칩 끝 빼기 표시 — 글자 "×" 대신 SVG(UI 가이드 4.1, 기준선 때문에 가운데에서 벗어나지 않게) */
const RemoveMark = styled(CloseIcon)`
  && {
    display: block;
    width: 14px;
    height: 14px;
    margin-left: 2px;
  }
`;

/** 폰은 화면 대부분을 덮는 고정 높이 — 키보드가 올라와도 맨 위 검색칸과 첫 결과가 보이게 */
const Sheet = styled.div<{ isPhone: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${({ isPhone }) => (isPhone ? '85vh' : '560px')};
  ${({ isPhone }) => (isPhone ? 'height: 85dvh;' : 'max-height: calc(100vh - 64px);')}
  box-sizing: border-box;
  font-family: inherit;
  color: ${COLOR.text};
  background: #fff;
`;

const SheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 16px 16px 12px;

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
  }

  button {
    display: inline-flex;
    border: none;
    background: none;
    padding: 6px;
    cursor: pointer;
    color: ${COLOR.sub};
  }
`;

const SearchInput = styled.input`
  flex-shrink: 0;
  width: calc(100% - 32px);
  height: 52px;
  margin: 0 16px;
  padding: 0 14px;
  border: 1px solid ${COLOR.border};
  border-radius: 12px;
  font-size: 16px; /* 16px 미만이면 iOS 가 입력칸을 확대한다 */
  font-family: inherit;
  color: ${COLOR.text};
  background-color: #fff;
  outline: none;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    border-color: ${COLOR.primary};
  }

  &::placeholder {
    color: ${COLOR.placeholder};
  }

  /* 크롬 기본 파란 × 지우개 숨김 — 글자는 지우기 키로 지운다 */
  &::-webkit-search-cancel-button,
  &::-webkit-search-decoration {
    -webkit-appearance: none;
    appearance: none;
  }
`;

/** 결과 목록만 스크롤된다 */
const ResultArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 16px 0;
  -webkit-overflow-scrolling: touch;
`;

/** 하단 [완료] — 목록을 아무리 내려도 시트 바닥에 붙어 있다 */
const SheetFoot = styled.div`
  position: sticky;
  bottom: 0;
  flex-shrink: 0;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid ${COLOR.lineSoft};
`;

const DoneButton = styled.button`
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 14px;
  background: ${COLOR.primary};
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
`;

const SHEET_TITLE_ID = 'station-search-sheet-title';

interface StationSearchSheetProps {
  isPhone: boolean;
  value: SubwayStation[];
  onChange: (next: SubwayStation[]) => void;
  onClose: () => void;
}

/**
 * 역 검색 시트 — 열릴 때만 그려지므로 검색어·결과는 열 때마다 새로 시작한다.
 * 입력이 멈추고 200ms 뒤 검색, 요청이 겹치면 마지막 요청의 결과만 쓴다.
 */
const StationSearchSheet: React.FC<StationSearchSheetProps> = ({ isPhone, value, onChange, onClose }) => {
  const [query, setQuery] = useState<string>('');
  const search: StationSearchState = useStationSearch(query);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedIds: Set<string> = useMemo(() => new Set(value.map((s) => s.id)), [value]);

  // 시트가 열리면 바로 입력할 수 있게 — 모달 포커스 가두기가 먼저 잡아도 입력칸으로 옮긴다
  useEffect(() => {
    const timer: number = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, []);

  const toggle = (station: SubwayStation): void => {
    if (selectedIds.has(station.id)) onChange(value.filter((s) => s.id !== station.id));
    else onChange([...value, { id: station.id, name: station.name, lineNo: station.lineNo }]);
  };

  return (
    <Sheet isPhone={isPhone}>
      <SheetHead>
        <h3 id={SHEET_TITLE_ID}>가까운 지하철역</h3>
        <button type="button" aria-label="닫기" onClick={onClose}>
          <CloseIcon />
        </button>
      </SheetHead>
      <SearchInput
        ref={inputRef}
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        placeholder="역 이름 (예: 강남, 서면)"
        aria-label="역 이름 검색"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ResultArea aria-live="polite">
        <StationResultList search={search} selectedIds={selectedIds} onToggle={toggle} />
      </ResultArea>
      <SheetFoot>
        <DoneButton type="button" onClick={onClose}>
          {value.length > 0 ? '선택' : '닫기'}
        </DoneButton>
      </SheetFoot>
    </Sheet>
  );
};

interface StationSearchFieldProps {
  /** 고른 역(이름·노선 포함) */
  value: SubwayStation[];
  onChange: (next: SubwayStation[]) => void;
  disabled?: boolean;
  /** 오류 시 스크롤 이동용 data-field */
  dataField?: string;
}

/**
 * 가까운 지하철역 — 고른 역 칩 + [+ 역 검색] 시트(폰은 아래 시트, PC 는 작은 창).
 * 전국 역을 이름으로 찾아 여러 개 고른다. 시트는 열릴 때만 그린다(항상 떠 있는 dialog 는 스와이프 뒤로가기를 막는다).
 */
const StationSearchField: React.FC<StationSearchFieldProps> = ({ value, onChange, disabled = false, dataField }) => {
  const isPhone: boolean = useMediaQuery('(max-width:768px)');
  const [open, setOpen] = useState<boolean>(false);
  const close = (): void => setOpen(false);

  const sheet = open && (
    <StationSearchSheet isPhone={isPhone} value={value} onChange={onChange} onClose={close} />
  );

  return (
    <>
      <ChipRow data-field={dataField}>
        {value.map((station) => (
          <Chip
            key={station.id}
            type="button"
            selected
            disabled={disabled}
            aria-label={`${stationLabel(station)} 빼기`}
            onClick={() => onChange(value.filter((s) => s.id !== station.id))}
          >
            <TrainOutlinedIcon aria-hidden />
            {station.name}
            {station.lineNo && <small>{station.lineNo}</small>}
            <RemoveMark aria-hidden />
          </Chip>
        ))}
        {/* 앞 아이콘 쪽 안쪽 여백 4px 줄임(UI 가이드 4.1) */}
        <Chip type="button" selected={false} disabled={disabled} onClick={() => setOpen(true)} style={{ paddingLeft: 10 }}>
          <AddIcon aria-hidden />
          역 검색
        </Chip>
      </ChipRow>

      {open && (isPhone ? (
        <Drawer
          anchor="bottom"
          open
          onClose={close}
          PaperProps={{ style: { borderRadius: '16px 16px 0 0' }, role: 'dialog', 'aria-labelledby': SHEET_TITLE_ID } as object}
        >
          {sheet}
        </Drawer>
      ) : (
        <Dialog open onClose={close} aria-labelledby={SHEET_TITLE_ID} PaperProps={{ style: { width: 440, borderRadius: 16, overflow: 'hidden' } }}>
          {sheet}
        </Dialog>
      ))}
    </>
  );
};

export default StationSearchField;
